import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { authService } from '../services/authService';

/**
 * TECNILAND - Authentication Hook & Context
 * Maneja el estado de autenticación global de la aplicación
 */

// Crear contexto de autenticación
const AuthContext = createContext(null);

/**
 * Provider de autenticación para envolver la aplicación
 */
export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Verifica si hay un token válido en localStorage
   */
  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      setLoading(false);
      setIsAuthenticated(false);
      setUser(null);
      return;
    }

    try {
      const response = await authService.validate(token);
      
      if (response.success && response.data?.valid) {
        setIsAuthenticated(true);
        setUser(response.data.user);
      } else {
        // Token inválido o expirado, limpiar
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('Error validating token:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      setIsAuthenticated(false);
      setUser(null);
    }
    
    setLoading(false);
  }, []);

  // Verificar autenticación al montar el componente
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Escuchar evento de cambio de username
  useEffect(() => {
    const handleUsernameChanged = (event) => {
      const newUsername = event.detail?.username;
      if (newUsername && user) {
        setUser(prevUser => ({
          ...prevUser,
          username: newUsername
        }));
      }
    };

    window.addEventListener('usernameChanged', handleUsernameChanged);
    return () => window.removeEventListener('usernameChanged', handleUsernameChanged);
  }, [user]);

  /**
   * Inicia sesión y guarda el token
   * @param {string} token - JWT token
   * @param {Object} userData - Datos del usuario
   */
  const login = useCallback((token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('username', userData.username);
    setIsAuthenticated(true);
    setUser(userData);
  }, []);

  /**
   * Cierra la sesión y limpia el estado
   */
  const logout = useCallback(async () => {
    const token = localStorage.getItem('token');
    
    if (token) {
      try {
        await authService.logout(token);
      } catch (error) {
        console.error('Error during logout:', error);
      }
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  /**
   * Cierra todas las sesiones del usuario
   */
  const logoutAll = useCallback(async () => {
    const token = localStorage.getItem('token');
    
    if (token) {
      try {
        await authService.logoutAll(token);
      } catch (error) {
        console.error('Error closing all sessions:', error);
      }
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  /**
   * Actualiza los datos del usuario en el contexto
   * @param {Object} userData - Nuevos datos del usuario
   */
  const updateUser = useCallback((userData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...userData
    }));
    // Actualizar localStorage si cambia el username
    if (userData.username) {
      localStorage.setItem('username', userData.username);
    }
  }, []);

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    logoutAll,
    checkAuth,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook para acceder al contexto de autenticación
 * @returns {Object} Estado y funciones de autenticación
 */
export function useAuth() {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  
  return context;
}

export default useAuth;
